import React, { JSX } from 'react';
import Image from 'next/image';
import { ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import ProfileBreadcrumb from '../shared/ProfileBreadcrumb';
import Icons from 'components/atoms/Icons';
import { ClinicalFocus } from 'components/types/ClinicalFocusTypes';
import { LocationItem } from 'components/types/LocationTypes';
import Constants from 'assets/utils/constant';
import { useEffect, useRef, useState } from 'react';

type ProviderHeroProps = {
  img: ImageField;
  name: string;
  degrees: string;
  specialties?: Array<string>;
  clinicalFocus: Array<ClinicalFocus>;
  rating: number;
  reviewCount: number;
  accepting: boolean;
  locations?: Array<LocationItem>;
  gender: string;
  sortField: any;
};

const getImageSrcFromHtml = (img?: ImageField): string => {
  const value = img?.value as unknown;

  if (typeof value !== 'string') return '';

  const match = value.match(/src=["']([^"']+)["']/);
  return match?.[1] || '';
};

const getImageAltFromHtml = (img?: ImageField): string => {
  const value = img?.value as unknown;

  if (typeof value !== 'string') return '';

  const match = value.match(/alt=["']([^"']*)["']/);
  return match?.[1] || '';
};

const getOfficeLocationAnchorId = (city?: string): string => {
  const normalizedCity = (city || 'location')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `office-location-${normalizedCity}`;
};

const ProviderHero = (props: ProviderHeroProps): JSX.Element => {
  const notEmployed =
    String(props?.sortField?.value).toLowerCase() !== 'employed';

  const [tipOpen, setTipOpen] = useState(false);
  const tipRef = useRef<HTMLSpanElement>(null);
  const cities = new Set<string>();

  props.locations?.forEach(location => {
    const city = location?.fields?.City?.value;

    if (city) {
      cities.add(city);
    }
  });

  const fallbackImage =
    props.gender == 'Male'
      ? Constants.fallbackImages.providerMale
      : Constants.fallbackImages.providerFemale;

  const providerImageSrc = getImageSrcFromHtml(props.img) || fallbackImage;
  const providerImageAlt =
    getImageAltFromHtml(props.img) || props.name || 'Provider image';

  const [imageSrc, setImageSrc] = useState<string>(providerImageSrc);

  useEffect(() => {
    setImageSrc(providerImageSrc);
  }, [providerImageSrc]);

  useEffect(() => {
    const oninfoClick = (e: MouseEvent) => {
      if (!tipRef.current) return;
      if (!tipRef.current.contains(e.target as Node)) setTipOpen(false);
    };
    document.addEventListener('mousedown', oninfoClick);
    return () => document.removeEventListener('mousedown', oninfoClick);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTipOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="overhauled">
      <div className="bg-primary provider-hero">
        <div className="top-section jss-inner-container">
          <ProfileBreadcrumb
            searchType={'providers'}
            name={props?.name + ', ' + props?.degrees}
          />
          <div
            className="hero-inner"
            itemScope
            itemType="http://schema.org/Physician"
          >
            <div className="profile-image">
              <Image
                itemProp="image"
                src={imageSrc}
                alt={providerImageAlt}
                fill
                sizes="(max-width: 767px) 100vw, (max-width: 1023px) 338px, (max-width: 1199px) 419px, (max-width: 1439px) 444px, 521px"
                style={{ objectFit: 'cover' }}
                unoptimized={true}
                onError={() => setImageSrc(fallbackImage)}
              />
            </div>
            <div className="result-top">
              <div className="result-details">
                {props.name ? (
                  <>
                    <h1 className="result-name" itemProp="name">
                      {(props?.name && props.name) +
                        ', ' +
                        (props?.degrees && props.degrees)}
                    </h1>
                    <p className="employee">
                      {notEmployed ? (
                        <>
                          Not employed by OSF HealthCare
                          <span
                            className={`info-tooltip ${tipOpen ? 'is-open' : ''}`}
                            ref={tipRef}
                          >
                            <button
                              type="button"
                              aria-label="More info about employment status"
                              aria-expanded={tipOpen}
                              aria-controls="employment-tip"
                              onClick={() => setTipOpen(v => !v)}
                            >
                              <Icons
                                id="information"
                                className="icon-information"
                              />
                            </button>
                            <div
                              id="employment-tip"
                              role="tooltip"
                              className="tooltip"
                              aria-hidden={!tipOpen}
                            >
                              <span
                                className="tooltip-arrow"
                                aria-hidden="true"
                              ></span>
                              <div className="tooltip-card">
                                <button
                                  type="button"
                                  className="tooltip-close"
                                  aria-label="Close"
                                  onClick={() => setTipOpen(false)}
                                >
                                  <Icons
                                    id="close"
                                    className="icon icon-close"
                                  />
                                </button>
                                <div className="tooltip-body">
                                  <p className="tooltip-text">
                                    This individual is not an employee or agent
                                    of OSF HealthCare. OSF HealthCare is not
                                    liable for the medical care provided or
                                    decisions made by these independent
                                    physicians.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </span>
                        </>
                      ) : (
                        <>Employed by OSF HealthCare</>
                      )}
                    </p>
                  </>
                ) : (
                  <></>
                )}
                {props?.specialties && props?.specialties?.length > 0 ? (
                  <div className="specialties">
                    <p className="header">Specialty: </p>

                    {props?.specialties &&
                      props?.specialties.map((specialty, index) => {
                        return (
                          <div
                            className="specialty"
                            itemProp="medicalSpecialty"
                            itemScope
                            itemType="https://schema.org/medicalSpecialty"
                            key={`specialty-${index}`}
                          >
                            <div
                              itemProp="code"
                              itemScope
                              itemType="https://schema.org/MedicalCode"
                            >
                              <p itemProp="description">
                                {specialty}
                                {props?.specialties &&
                                  props?.specialties?.length > 1 &&
                                  index != props?.specialties.length - 1 &&
                                  ', '}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  ''
                )}
                {props?.clinicalFocus && props?.clinicalFocus?.length > 0 ? (
                  <div className="clinical-focuses">
                    <p className="header">Clinical Area of Focus: </p>
                    {props?.clinicalFocus.map((focus, index) => {
                      return (
                        <div
                          className="clinical-focus"
                          itemProp="medicalSpecialty"
                          itemScope
                          itemType="https://schema.org/medicalSpecialty"
                          key={`focus-${index}`}
                        >
                          <p itemProp="description">
                            {focus.fields.ClinicalFocusName.value}
                            {props?.clinicalFocus.length > 1 &&
                              index != props?.clinicalFocus.length - 1 &&
                              ', '}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  ''
                )}
                {props?.rating && props?.rating > 0 ? (
                  <div className="ratings">
                    <div
                      className="rating-stars"
                      ie-style={`--rating: ${props?.rating}`}
                      title={`This provider is rated ${props?.rating} out of 5.`}
                      aria-label={`This provider is rated ${props?.rating}out of 5.`}
                      style={
                        { '--rating': props?.rating } as React.CSSProperties
                      }
                    >
                      <div className="star">
                        <span className="icon icon-star">
                          <Icons id="star" />
                        </span>
                        <div className="star-fill">
                          <span className="icon icon-star">
                            <Icons id="star" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <a href="#ratings" className="average-rating">
                      {props?.rating} ({props?.reviewCount})
                    </a>
                  </div>
                ) : (
                  ''
                )}
                {cities.size > 0 ? (
                  <div className="locations">
                    <span className="icon icon-location" aria-hidden="true">
                      <Icons id="location" />
                    </span>

                    <div className="locations-content">
                      <span className="locations-label">Office Locations: </span>

                      <div className="city-list">
                        {Array.from(cities).map((city, index) => (
                          <div className="city" key={`city-${index}`}>
                            {index > 0 && (
                              <span className="city-separator">, </span>
                            )}

                            <a href={`#${getOfficeLocationAnchorId(city)}`}>
                              {city}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                {props?.accepting ? (
                  <div className="new-patient">
                    <span className=" icon-checkmark" aria-hidden="true">
                      <Icons id="icon-checkmark-circle" />
                    </span>
                    <p className="new-patient-text">
                      Accepting New Patients
                    </p>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderHero;